import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { useActiveCompany } from "@/contexts/ActiveCompanyContext";
import { useCompanyPermissions } from "@/hooks/useCompanyPermissions";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface CompanySwitcherProps {
  showLabel?: boolean;
  variant?: "dropdown" | "inline";
}

export function CompanySwitcher({ showLabel = false, variant = "dropdown" }: CompanySwitcherProps) {
  const { activeCompany, setActiveCompany, companies, hasMultipleCompanies } = useActiveCompany();
  const [open, setOpen] = useState(false);

  // Don't show switcher if user has only one company
  if (!hasMultipleCompanies) {
    return null;
  }

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-2">
        {showLabel && <span className="text-sm text-muted-foreground">Company:</span>}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-[250px] justify-between"
            >
              <div className="flex items-center gap-2 overflow-hidden">
                {activeCompany?.company.logo_url ? (
                  <img 
                    src={activeCompany.company.logo_url} 
                    alt=""
                    className="h-4 w-4 object-contain flex-shrink-0"
                  />
                ) : (
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                )}
                <span className="truncate">{activeCompany?.company.name}</span>
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[250px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search companies..." />
              <CommandList>
                <CommandEmpty>No companies found.</CommandEmpty>
                <CommandGroup>
                  {companies.map((company) => {
                    const highestRole = company.roles.sort((a, b) => {
                      const roleOrder = { owner: 0, admin: 1, manager: 2, recruiter: 3, member: 4 };
                      return roleOrder[a.role] - roleOrder[b.role];
                    })[0]?.role || 'member';

                    return (
                      <CommandItem
                        key={company.id}
                        value={company.company.name}
                        onSelect={() => {
                          setActiveCompany(company);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            activeCompany?.id === company.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex items-center gap-2 flex-1 overflow-hidden">
                          {company.company.logo_url ? (
                            <img 
                              src={company.company.logo_url} 
                              alt=""
                              className="h-4 w-4 object-contain flex-shrink-0"
                            />
                          ) : (
                            <Building2 className="h-4 w-4 flex-shrink-0" />
                          )}
                          <span className="truncate">{company.company.name}</span>
                        </div>
                        <Badge variant="outline" className="ml-2 text-xs capitalize">
                          {highestRole}
                        </Badge>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          <div className="flex items-center gap-2 overflow-hidden">
            {activeCompany?.company.logo_url ? (
              <img 
                src={activeCompany.company.logo_url} 
                alt=""
                className="h-4 w-4 object-contain flex-shrink-0"
              />
            ) : (
              <Building2 className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="truncate text-sm">{activeCompany?.company.name}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0 z-50 bg-background" align="start">
        <Command>
          <CommandInput placeholder="Search companies..." />
          <CommandList>
            <CommandEmpty>No companies found.</CommandEmpty>
            <CommandGroup>
              {companies.map((company) => {
                const highestRole = company.roles.sort((a, b) => {
                  const roleOrder = { owner: 0, admin: 1, manager: 2, recruiter: 3, member: 4 };
                  return roleOrder[a.role] - roleOrder[b.role];
                })[0]?.role || 'member';

                return (
                  <CommandItem
                    key={company.id}
                    value={company.company.name}
                    onSelect={() => {
                      setActiveCompany(company);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        activeCompany?.id === company.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex items-center gap-2 flex-1 overflow-hidden">
                      {company.company.logo_url ? (
                        <img 
                          src={company.company.logo_url} 
                          alt=""
                          className="h-4 w-4 object-contain flex-shrink-0"
                        />
                      ) : (
                        <Building2 className="h-4 w-4 flex-shrink-0" />
                      )}
                      <span className="truncate">{company.company.name}</span>
                    </div>
                    <Badge variant="outline" className="ml-2 text-xs capitalize">
                      {highestRole}
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
